(() => {
  'use strict';
  const pending = {};
  const allowed = { smile: false, omnisend: false };
  const held = { smile: [], omnisend: [] };
  const originalAppend = Node.prototype.appendChild;
  const originalInsert = Node.prototype.insertBefore;
  const owner = node => {
    if (node?.tagName !== 'SCRIPT' || !node.src) return null;
    const url = new URL(node.src, document.baseURI);
    if (url.hostname === 'js.smile.io' && url.pathname === '/v1/smile-shopify.js') return 'smile';
    if (url.hostname === 'omnisnippet1.com' && url.pathname === '/platforms/shopify.js') return 'omnisend';
    return null;
  };
  const hold = (node, parent, before) => {
    try {
      const name = owner(node);
      if (!name || allowed[name]) return false;
      if (!held[name].some(entry => entry.node === node)) held[name].push({ node, parent, before });
      return true;
    } catch { return false; }
  };
  Node.prototype.appendChild = function (node) {
    return hold(node, this, null) ? node : originalAppend.call(this, node);
  };
  Node.prototype.insertBefore = function (node, before) {
    return hold(node, this, before) ? node : originalInsert.call(this, node, before);
  };
  const release = name => {
    allowed[name] = true;
    for (const entry of held[name].splice(0)) {
      const parent = entry.parent.isConnected ? entry.parent : document.head;
      const before = entry.before?.parentNode === parent ? entry.before : null;
      // Sonradan kurulan başka DOM izleyicileri de çağrı zincirinde kalır.
      parent.insertBefore(entry.node, before);
    }
  };
  const status = document.querySelector('[data-testid="dk-app-status"]');
  const smileButton = document.querySelector('[data-testid="dk-smile-trigger"]');
  const announce = text => { if (status) status.textContent = text; };
  const busy = (element, value) => {
    if (!element) return;
    element.setAttribute('aria-busy', String(value));
    element.setAttribute('aria-disabled', String(value));
  };
  const waitFor = (check, deadline = Date.now() + 20000) => new Promise((resolve, reject) => {
    const poll = () => {
      if (check()) resolve();
      else if (Date.now() >= deadline) reject(new Error('Uygulama hazır değil'));
      else setTimeout(poll, 50);
    };
    poll();
  });
  const once = (name, load) => {
    if (!pending[name]) pending[name] = load().catch(error => { delete pending[name]; throw error; });
    return pending[name];
  };
  const loadOmnisend = () => once('omnisend', async () => {
    release('omnisend');
    await waitFor(() => window.OMNISEND_LAUNCHER_LOADED === true);
  });
  const within = async (work, deadline) => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) throw new Error('Uygulama hazır değil');
    let timer;
    try {
      return await Promise.race([Promise.resolve().then(work), new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Uygulama hazır değil')), remaining);
      })]);
    } finally { clearTimeout(timer); }
  };
  const loadSmile = (deadline, useDeepLink) => once('smile', async () => {
    release('smile');
    await waitFor(() => typeof window.SmileUI?.ready === 'function' || document.getElementById('smile-lite-launcher-frame')?.getBoundingClientRect().height > 0, deadline);
    if (typeof window.SmileUI?.ready !== 'function') useDeepLink();
    await waitFor(() => typeof window.SmileUI?.ready === 'function', deadline);
    await within(() => window.SmileUI.ready(), deadline);
  });
  const nativeLauncherVisible = () => [...document.querySelectorAll('#smile-lite-launcher-frame, iframe.smile-launcher-frame')].some(frame => {
    const rect = frame.getBoundingClientRect(), style = getComputedStyle(frame);
    if (style.visibility !== 'visible' || style.pointerEvents === 'none' || rect.width <= 0 || rect.height <= 0 ||
        rect.right <= 0 || rect.bottom <= 0 || rect.left >= innerWidth || rect.top >= innerHeight) return false;
    // visibility çocukta tekrar açılabilir; display/opacity bütün alt ağacı etkiler.
    for (let node = frame; node; node = node.parentElement) {
      const current = getComputedStyle(node);
      if (current.display === 'none' || Number(current.opacity) === 0) return false;
    }
    return true;
  });
  let launcherObserver;
  const followNativeLauncher = () => {
    if (!smileButton || launcherObserver) return;
    const tracked = new WeakSet();
    const sync = () => {
      for (const frame of document.querySelectorAll('#smile-lite-launcher-frame, iframe.smile-launcher-frame')) {
        for (let node = frame; node && node !== document.body; node = node.parentElement) {
          if (tracked.has(node)) continue;
          tracked.add(node);
          launcherObserver.observe(node, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        }
      }
      smileButton.hidden = nativeLauncherVisible() && !smileButton.contains(document.activeElement);
    };
    launcherObserver = new MutationObserver(sync);
    launcherObserver.observe(document.body, { childList: true, subtree: true });
    smileButton.addEventListener('blur', sync);
    for (const event of ['transitionend', 'animationend']) document.body.addEventListener(event, e => { if (tracked.has(e.target)) sync(); }, true);
    sync();
  };
  const openSmile = async () => {
    if (smileButton?.getAttribute('aria-disabled') === 'true') return;
    const deadline = Date.now() + 12000;
    const originalURL = new URL(location.href);
    let fallbackURL;
    const useDeepLink = () => {
      const next = new URL(location.href); next.hash = 'smile-home';
      const previous = location.href; fallbackURL = next.href;
      history.replaceState(history.state, '', fallbackURL);
      window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL: previous, newURL: fallbackURL }));
    };
    busy(smileButton, true);
    announce('Kitap Puan yükleniyor…');
    try {
      await loadSmile(deadline, useDeepLink);
      await within(() => window.SmileUI.openPanel(), deadline);
      announce('Kitap Puan açıldı.');
    } catch {
      announce('Kitap Puan şu anda açılamadı. Sayfayı yenileyip yeniden deneyin.');
    } finally {
      followNativeLauncher();
      // Yalnız kendi geçici çapasını geri al; arada başka bir adrese/çapaya gidişi ezme.
      if (fallbackURL && location.pathname === originalURL.pathname && location.search === originalURL.search &&
          (location.href === fallbackURL || location.hash === '')) history.replaceState(history.state, '', originalURL.href);
      busy(smileButton, false);
    }
  };
  smileButton?.addEventListener('click', openSmile);
  const onScroll = () => {
    if (window.scrollY < 24) return;
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('pointermove', onPointerMove);
    // Mevcut otomatik formun 34 sn / çıkış niyeti kuralı değiştirilmez.
    loadOmnisend().catch(() => {});
  };
  const onPointerMove = () => {
    window.removeEventListener('scroll', onScroll);
    loadOmnisend().catch(() => {});
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  if (window.matchMedia('(pointer: fine)').matches) window.addEventListener('pointermove', onPointerMove, { once: true, passive: true });
  window.addEventListener('pageshow', onScroll);
  onScroll();
  if (/^#smile-/.test(location.hash) || new URL(location.href).searchParams.has('smile_deep_link')) openSmile();
})();
